import { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Bell,
  MonitorPlay,
  PanelTop,
  Mail,
  MessageSquare,
  Search,
  Megaphone,
  AlertTriangle,
  AlertOctagon,
  Info,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Drawer } from '../../components/Drawer';
import { Modal } from '../../components/Modal';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import type {
  Broadcast,
  BroadcastAudienceKind,
  NotificationSeverity,
} from '../../mocks/clientBatch';
import { useBroadcasts, createBroadcast, cancelBroadcast, updateBroadcast } from '../../api/platform';
import { useResellers, useAdminBusinesses } from '../../api/admin';
import type { Reseller, AdminBusiness } from '../../mocks/admin';

type ChannelKey = 'notification_panel' | 'login_modal' | 'top_banner' | 'email' | 'sms';

/** One row of GET /admin/broadcasts, derived so the edit drawer can't drift from it. */
type BroadcastRow = ReturnType<typeof useBroadcasts>['data'] extends (infer T)[] | undefined ? T : never;

const CHANNEL_META: Record<ChannelKey, { label: string; Icon: typeof Bell }> = {
  notification_panel: { label: 'In-app notification panel', Icon: Bell },
  login_modal: { label: 'After-login modal banner', Icon: MonitorPlay },
  top_banner: { label: 'Top-of-page dismissible banner', Icon: PanelTop },
  email: { label: 'Email', Icon: Mail },
  sms: { label: 'SMS (BoomCast)', Icon: MessageSquare },
};

const SEVERITY_BADGE: Record<NotificationSeverity, 'info' | 'warning' | 'error'> = {
  info: 'info',
  warning: 'warning',
  critical: 'error',
};

function audienceLabel(b: Broadcast): string {
  switch (b.audienceKind) {
    case 'all_resellers':
      return 'All resellers';
    case 'all_end_customers':
      return 'All end customers';
    case 'reseller_customers':
      return `Customers of ${b.audienceTargetLabel ?? 'reseller'}`;
    case 'one_business':
      return b.audienceTargetLabel ?? 'One business';
  }
}

/** Admin Broadcasts CRUD. */
export function Broadcasts() {
  const { data: broadcastList = [] } = useBroadcasts();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [editing, setEditing] = useState<BroadcastRow | null>(null);

  const filtered = useMemo(() => {
    if (!search) return broadcastList;
    const q = search.toLowerCase();
    return broadcastList.filter(
      (b) =>
        b.title.toLowerCase().includes(q) ||
        b.body.toLowerCase().includes(q) ||
        audienceLabel(b).toLowerCase().includes(q),
    );
  }, [broadcastList, search]);

  const items = broadcastList;

  async function handleCancel(id: string) {
    setCancelingId(id);
    try {
      await cancelBroadcast(id);
      await qc.invalidateQueries({ queryKey: ['admin', 'broadcasts'] });
    } catch {
      // leave the row; a sent broadcast can't be canceled (409)
    } finally {
      setCancelingId(null);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Communications"
        title="Broadcasts"
        subtitle="Send announcements to platform users."
        action={
          <Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setComposeOpen(true)}>
            New broadcast
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <span className="text-xs text-zinc-500 dark:text-zinc-400 inline-flex items-center gap-1.5">
            <Megaphone className="w-3.5 h-3.5" />
            {items.length} broadcast{items.length === 1 ? '' : 's'}
          </span>
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search title, body, audience…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Title</th>
                <th className="text-left font-medium px-4 py-3">Audience</th>
                <th className="text-left font-medium px-4 py-3">Severity</th>
                <th className="text-left font-medium px-4 py-3">Channels</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3">Show until</th>
                <th className="text-left font-medium px-4 py-3">Read</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {filtered.map((b) => {
                const isSent = Boolean(b.sentAt);
                return (
                  <tr key={b.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-5 sm:px-6 py-3 max-w-[20rem]">
                      <div className="font-medium text-zinc-950 dark:text-zinc-50 truncate">{b.title}</div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                        {b.body.length > 80 ? b.body.slice(0, 80) + '…' : b.body}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{audienceLabel(b)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={SEVERITY_BADGE[b.severity]}>
                        {b.severity.charAt(0).toUpperCase() + b.severity.slice(1)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-zinc-500 dark:text-zinc-400">
                        {b.deliveryChannels.map((c) => {
                          const meta = CHANNEL_META[c];
                          const Icon = meta.Icon;
                          return (
                            <span
                              key={c}
                              title={meta.label}
                              className="inline-flex w-6 h-6 grid place-items-center rounded bg-zinc-100 dark:bg-zinc-800"
                            >
                              <Icon className="w-3.5 h-3.5" />
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {isSent ? (
                        <div>
                          <div className="text-zinc-700 dark:text-zinc-200">Sent</div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
                            {fmtDate(b.sentAt)}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-zinc-700 dark:text-zinc-200">Scheduled</div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
                            {fmtDate(b.scheduledAt)}
                          </div>
                        </div>
                      )}
                    </td>
                    {/* "Show until" (#16). It was stored and enforced but never
                        displayed, so an admin could not tell a permanent
                        announcement from one that expires — or see that a live
                        one had already lapsed. */}
                    <td className="px-4 py-3 tabular-nums">
                      {b.pinnedUntil ? (
                        <span
                          className={
                            new Date(b.pinnedUntil) < new Date()
                              ? 'text-zinc-400 dark:text-zinc-500 line-through'
                              : 'text-zinc-700 dark:text-zinc-200'
                          }
                          title={
                            new Date(b.pinnedUntil) < new Date()
                              ? 'Expired — no longer shown to recipients'
                              : 'Stops showing on this date'
                          }
                        >
                          {fmtDate(b.pinnedUntil)}
                        </span>
                      ) : (
                        <span className="text-zinc-400 dark:text-zinc-500" title="No expiry — shows until dismissed">
                          Always
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">
                      {isSent ? `${b.readCount} of ${b.recipientCount}` : '—'}
                    </td>
                    <td className="px-5 sm:px-6 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* A delivered broadcast is no longer fully editable — the
                            message is already in recipients' hands — but its
                            "Show until" still is, which is the thing an admin
                            actually wants to change about a live announcement. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(b)}
                          title={isSent ? 'Delivered — only “Show until” can be changed' : 'Edit this broadcast'}
                        >
                          Edit
                        </Button>
                        {!isSent && (
                          <Button variant="ghost" size="sm" onClick={() => handleCancel(b.id)} loading={cancelingId === b.id}>
                            Cancel
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 sm:px-6 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No broadcasts match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <ComposeBroadcastDrawer
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onCreated={() => {
          void qc.invalidateQueries({ queryKey: ['admin', 'broadcasts'] });
          setComposeOpen(false);
        }}
      />

      <EditBroadcastDrawer
        broadcast={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ['admin', 'broadcasts'] });
          setEditing(null);
        }}
      />
    </div>
  );
}

/** `2026-08-04T18:00:00.000Z` -> `2026-08-04T18:00`, the value a datetime-local wants. */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Edit a broadcast (#16).
 *
 * Two modes, decided by whether it has been delivered:
 *   - NOT delivered — everything is editable; nobody has seen it yet.
 *   - delivered      — only "Show until". The message is already in recipients'
 *                      inboxes and notification panels, so editing the wording
 *                      afterwards would leave this list claiming something
 *                      different from what was actually sent. The expiry is a
 *                      genuine exception: it is read live on every request, so
 *                      changing it really does take effect, and it is the thing
 *                      an admin most often wants to change about a live notice.
 *
 * The server enforces the same split; this only reflects it, so the read-only
 * fields explain themselves rather than silently 409-ing on save.
 */
function EditBroadcastDrawer({
  broadcast,
  onClose,
  onSaved,
}: {
  broadcast: BroadcastRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const sent = Boolean(broadcast?.sentAt);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [showUntil, setShowUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever a different row is opened.
  useEffect(() => {
    if (!broadcast) return;
    setTitle(broadcast.title);
    setBody(broadcast.body);
    setShowUntil(toLocalInput(broadcast.pinnedUntil));
    setError(null);
  }, [broadcast]);

  const originalShowUntil = toLocalInput(broadcast?.pinnedUntil);
  const dirty = broadcast
    ? showUntil !== originalShowUntil || (!sent && (title !== broadcast.title || body !== broadcast.body))
    : false;

  async function save() {
    if (!broadcast) return;
    setBusy(true);
    setError(null);
    try {
      await updateBroadcast(broadcast.id, {
        // Only send what actually changed — a delivered broadcast rejects content
        // fields, and re-posting an unchanged title would trip that for no reason.
        ...(!sent && title !== broadcast.title ? { title: title.trim() } : {}),
        ...(!sent && body !== broadcast.body ? { body: body.trim() } : {}),
        ...(showUntil !== originalShowUntil
          ? { pinnedUntil: showUntil ? new Date(showUntil).toISOString() : null }
          : {}),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the broadcast. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      open={!!broadcast}
      onClose={() => { if (!busy) onClose(); }}
      title="Edit broadcast"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
          <Button onClick={() => void save()} loading={busy} disabled={busy || !dirty}>Save changes</Button>
        </>
      }
    >
      <div className="space-y-5">
        {sent && (
          <Banner tone="info" title="Already delivered">
            This announcement has been sent, so its wording and channels are fixed — recipients already
            have the original. <strong>“Show until” can still be changed</strong>, and takes effect
            immediately for everyone.
          </Banner>
        )}

        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={sent}
        />

        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={sent}
            rows={5}
            className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand resize-none disabled:opacity-60"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Show until</label>
          <input
            type="datetime-local"
            value={showUntil}
            onChange={(e) => setShowUntil(e.target.value)}
            className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            After this moment the announcement stops appearing in the notification panel, the login
            modal and the top banner. Leave empty to show it until each user dismisses it.
          </p>
          {showUntil && (
            <button
              type="button"
              onClick={() => setShowUntil('')}
              className="mt-1.5 text-xs text-brand dark:text-brand-dm hover:underline"
            >
              Clear expiry
            </button>
          )}
        </div>

        {error && <Banner tone="error">{error}</Banner>}
      </div>
    </Drawer>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/* ── Compose drawer ──────────────────────────────────────────────── */

function ComposeBroadcastDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { data: resellers = [] } = useResellers();
  const { data: adminBusinesses = [] } = useAdminBusinesses();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<NotificationSeverity>('info');
  const [audienceKind, setAudienceKind] = useState<BroadcastAudienceKind>('all_resellers');
  const [resellerId, setResellerId] = useState<string>('');
  const [businessId, setBusinessId] = useState<string>('');

  // Default the audience selectors to the first option once data lands.
  useEffect(() => {
    if (!resellerId && resellers[0]) setResellerId(resellers[0].id);
  }, [resellers, resellerId]);
  useEffect(() => {
    if (!businessId && adminBusinesses[0]) setBusinessId(adminBusinesses[0].id);
  }, [adminBusinesses, businessId]);
  const [channels, setChannels] = useState<Record<ChannelKey, boolean>>({
    notification_panel: true,
    login_modal: true,
    top_banner: false,
    email: false,
    sms: false,
  });
  const [sendMode, setSendMode] = useState<'now' | 'schedule'>('now');
  const [scheduleAt, setScheduleAt] = useState('');
  const [showUntil, setShowUntil] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setTitle('');
    setBody('');
    setSeverity('info');
    setAudienceKind('all_resellers');
    setChannels({ notification_panel: true, login_modal: true, top_banner: false, email: false, sms: false });
    setSendMode('now');
    setScheduleAt('');
    setShowUntil('');
    setSubmitting(false);
    setDone(false);
    setError(null);
  }

  function handleClose() {
    if (submitting) return;
    reset();
    onClose();
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    const audienceTarget =
      audienceKind === 'reseller_customers'
        ? resellers.find((r) => r.id === resellerId)
        : audienceKind === 'one_business'
        ? adminBusinesses.find((bb) => bb.id === businessId)
        : null;
    try {
      await createBroadcast({
        audienceKind,
        audienceTargetId: audienceTarget?.id ?? null,
        audienceTargetLabel: audienceTarget?.name ?? null,
        title: title.trim(),
        body: body.trim(),
        severity,
        deliveryChannels: (Object.keys(channels) as ChannelKey[]).filter((k) => channels[k]),
        scheduledAt: sendMode === 'now' ? null : scheduleAt ? new Date(scheduleAt).toISOString() : null,
        pinnedUntil: showUntil ? new Date(showUntil).toISOString() : null,
      });
      setDone(true);
      // give the user a beat to see the success state, then refresh the list
      setTimeout(() => {
        onCreated();
        reset();
      }, 700);
    } catch {
      setError('Could not save the broadcast. Please try again.');
      setSubmitting(false);
    }
  }

  const canSubmit =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    Object.values(channels).some(Boolean) &&
    (sendMode === 'now' || scheduleAt.length > 0);

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        title="New broadcast"
        width="lg"
        footer={
          done ? (
            <span className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">Saved.</span>
          ) : (
            <>
              {error && <span className="text-sm text-rose-600 dark:text-rose-400 mr-auto">{error}</span>}
              <Button variant="secondary" onClick={() => setPreviewOpen(true)} disabled={!title || !body}>
                Preview
              </Button>
              <Button variant="ghost" onClick={handleClose} disabled={submitting}>Cancel</Button>
              <Button onClick={handleSubmit} loading={submitting} disabled={!canSubmit}>
                {sendMode === 'now' ? 'Send' : 'Schedule'}
              </Button>
            </>
          )
        }
      >
        <div className="space-y-5">
          <Input
            label="Title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Maintenance window — 04:00 Sun"
            maxLength={120}
          />

          <div>
            <label className="block">
              <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                Body <span className="text-rose-600 ml-0.5">*</span>
              </span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                placeholder="Markdown supported. Brief context, then a clear call to action."
                className="block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand dark:focus:ring-brand-dm dark:focus:border-brand-dm transition"
              />
            </label>
          </div>

          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Severity</span>
            <div className="flex flex-wrap gap-2">
              {(['info', 'warning', 'critical'] as NotificationSeverity[]).map((s) => (
                <label
                  key={s}
                  className={cn(
                    'inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm cursor-pointer transition',
                    severity === s
                      ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
                      : 'border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/70',
                  )}
                >
                  <input
                    type="radio"
                    name="severity"
                    checked={severity === s}
                    onChange={() => setSeverity(s)}
                    className="text-brand focus:ring-brand"
                  />
                  <span className="capitalize">{s}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              Audience <span className="text-rose-600 ml-0.5">*</span>
            </span>
            <div className="space-y-2">
              <AudienceRadio
                label="All resellers"
                checked={audienceKind === 'all_resellers'}
                onChange={() => setAudienceKind('all_resellers')}
              />
              <AudienceRadio
                label="All end customers"
                checked={audienceKind === 'all_end_customers'}
                onChange={() => setAudienceKind('all_end_customers')}
              />
              <AudienceRadio
                label="Customers of reseller"
                checked={audienceKind === 'reseller_customers'}
                onChange={() => setAudienceKind('reseller_customers')}
                trailing={
                  audienceKind === 'reseller_customers' && (
                    <SelectInline value={resellerId} onChange={setResellerId} options={resellers.map((r) => ({ value: r.id, label: r.name }))} />
                  )
                }
              />
              <AudienceRadio
                label="Specific business"
                checked={audienceKind === 'one_business'}
                onChange={() => setAudienceKind('one_business')}
                trailing={
                  audienceKind === 'one_business' && (
                    <SelectInline value={businessId} onChange={setBusinessId} options={adminBusinesses.map((b) => ({ value: b.id, label: b.name }))} />
                  )
                }
              />
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              Delivery channels
            </span>
            <div className="space-y-2">
              {(Object.keys(CHANNEL_META) as ChannelKey[]).map((c) => {
                const meta = CHANNEL_META[c];
                const Icon = meta.Icon;
                return (
                  <label
                    key={c}
                    className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={channels[c]}
                      onChange={(e) => setChannels((prev) => ({ ...prev, [c]: e.target.checked }))}
                      className="text-brand focus:ring-brand"
                    />
                    <Icon className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />
                    {meta.label}
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Schedule</span>
            <div className="space-y-2">
              <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="radio"
                  name="schedule"
                  checked={sendMode === 'now'}
                  onChange={() => setSendMode('now')}
                  className="text-brand focus:ring-brand"
                />
                Send now
              </label>
              <label className="flex flex-wrap items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="radio"
                  name="schedule"
                  checked={sendMode === 'schedule'}
                  onChange={() => setSendMode('schedule')}
                  className="text-brand focus:ring-brand"
                />
                Schedule for
                <input
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={(e) => {
                    setScheduleAt(e.target.value);
                    if (e.target.value) setSendMode('schedule');
                  }}
                  className="px-2 py-1 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700"
                />
              </label>
            </div>
          </div>

          <Input
            label="Show until"
            type="datetime-local"
            value={showUntil}
            onChange={(e) => setShowUntil(e.target.value)}
            hint="Optional: pinned banner / login-modal will dismiss after this time."
          />
        </div>
      </Drawer>

      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Preview · After-login modal"
        size="md"
        footer={<Button onClick={() => setPreviewOpen(false)}>Close preview</Button>}
      >
        <BroadcastPreview title={title || 'Untitled'} body={body} severity={severity} />
      </Modal>
    </>
  );
}

function AudienceRadio({
  label,
  checked,
  onChange,
  trailing,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  trailing?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 p-2.5 rounded-md border transition',
        checked
          ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
          : 'border-zinc-200 dark:border-zinc-700',
      )}
    >
      <label className="flex items-center gap-2.5 text-sm text-zinc-800 dark:text-zinc-100 cursor-pointer flex-1 min-w-0">
        <input
          type="radio"
          name="audience"
          checked={checked}
          onChange={onChange}
          className="text-brand focus:ring-brand"
        />
        {label}
      </label>
      {trailing}
    </div>
  );
}

function SelectInline({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-2 py-1 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function BroadcastPreview({
  title,
  body,
  severity,
}: {
  title: string;
  body: string;
  severity: NotificationSeverity;
}) {
  const Icon = severity === 'critical' ? AlertOctagon : severity === 'warning' ? AlertTriangle : Info;
  const tone = severity === 'critical' ? 'text-rose-600 dark:text-rose-400' : severity === 'warning' ? 'text-amber-600 dark:text-amber-400' : 'text-sky-600 dark:text-sky-400';
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <Icon className={cn('w-6 h-6 flex-shrink-0', tone)} />
        <div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">{title}</h3>
        </div>
      </div>
      <p className="text-sm text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap">
        {body || <span className="italic text-zinc-400">(empty body)</span>}
      </p>
    </div>
  );
}
